import type { Db } from "@/db/client";
import { round2 } from "./wholesale";
import { httpError } from "./http-error";

export type PricedItem = {
  listPrice: number; quantity: number; discountPct: number;
  unitPrice: number; lineTotal: number; priceOverride: boolean;
};

const bad = (msg: string): Error => httpError(msg, 400);

export function resolveDiscountPct(o: { storeDiscountPct: number | null | undefined; partnerDiscountPct: number }): number {
  return o.storeDiscountPct ?? o.partnerDiscountPct;
}

/** Cijena s partnerskim rabatom, zaokružena na cijeli fening. `null` prolazi kroz oba ulaza:
 *  bez cijene (artikal je nema) ili bez rabata (pozivalac nije partner) nema šta da se računa —
 *  0% rabata je i dalje broj i MORA dati `price` netaknut, ne `null` (razlika između "partner s
 *  0% rabatom" i "ovo nije partner" se ovdje ne smije izgubiti). Dijele je katalog
 *  (`superstore-catalog.ts`: kartica i detalj artikla) i korpa (`partner-cart.ts`), da isti
 *  izraz ne bude prepisan na tri mjesta. */
export function applyDiscountPct(price: number | null, pct: number | null): number | null {
  if (price === null || pct === null) return null;
  return Math.round((price * (100 - pct)) / 100);
}

export function priceItem(i: { listPrice: number; quantity: number; discountPct: number; overridePrice?: number | null }): PricedItem {
  if (!Number.isInteger(i.quantity) || i.quantity < 1) throw bad("Količina mora biti cijeli broj ≥ 1");
  if (i.discountPct < 0 || i.discountPct > 100) throw bad("Rabat mora biti između 0 i 100");
  if (i.listPrice < 0) throw bad("Cijena ne može biti negativna");
  const hasOverride = i.overridePrice !== null && i.overridePrice !== undefined;
  if (hasOverride && (i.overridePrice as number) < 0) throw bad("Cijena ne može biti negativna");
  const unitPrice = hasOverride ? round2(i.overridePrice as number) : round2(i.listPrice * (1 - i.discountPct / 100));
  return { listPrice: i.listPrice, quantity: i.quantity, discountPct: i.discountPct, unitPrice, lineTotal: round2(unitPrice * i.quantity), priceOverride: hasOverride };
}

export function orderTotals(items: { listPrice: number; quantity: number; lineTotal: number }[]) {
  const subtotal = round2(items.reduce((s, it) => s + it.listPrice * it.quantity, 0));
  const total = round2(items.reduce((s, it) => s + it.lineTotal, 0));
  return { subtotal, discountTotal: round2(subtotal - total), total };
}

export function vatBreakdown(total: number, vatRate: number) {
  const net = round2(total / (1 + vatRate / 100));
  return { net, vat: round2(total - net) };
}

/** PDV stopa iz postavki veleprodaje; 17% dok postavka ne postoji. Jedino mjesto koje čita
 *  `wholesale_settings.vat_rate` — dijele ga `WholesaleOrderService.getVatRate()` i
 *  provjera korpe (`src/services/partner-cart.ts`), da isto pravilo ne bude prepisano na
 *  dva mjesta (Ruling R22). */
export const VAT_RATE_SQL = "SELECT vat_rate FROM wholesale_settings WHERE id='default'";

export async function getVatRate(db: Db): Promise<number> {
  return (await db.get<{ vat_rate: number }>(VAT_RATE_SQL))?.vat_rate ?? 17;
}
